<script>
import {
  isColumnVisible,
  shouldShowActions,
  countVisibleColumns,
} from '@modules/consolidator/utils/column-visibility/column-visibility'
import {
  SALDO_BRUTO_COLUMNS,
  RENTABILIDADE_COLUMNS,
  WALLET_ASSET_COLUMN_CONFIG,
  buildRenderUnits,
} from '@modules/consolidator/constants/wallet-asset-columns'

export default {
  name: 'AssetsByGroupTableHeader',
  props: {
    readonly: { type: Boolean, default: false },
    visibleColumns: { type: Array, default: () => [] },
    // Allowlist de acoes por CHAVE (`detalhes`, `editar`, ...). `null` = default,
    // o conjunto inteiro gateado por `readonly`. Array = exatamente essas.
    allowedActions: { type: Array, default: null },
    // Catalogo INJETAVEL: as unidades de render do cabecalho derivam DELE, senao
    // uma coluna nova no catalogo do consumidor nao ganharia cabecalho.
    columnsConfig: { type: Array, default: () => WALLET_ASSET_COLUMN_CONFIG },
    isAllSelected: { type: Boolean, default: false },
    isSomeSelected: { type: Boolean, default: false },
    modoFiltroCorretora: { type: String, default: '' },
    showRentability: { type: Boolean, default: false },
    loadingRentAsset: { type: Boolean, default: false },
    recomendacaoTooltip: { type: String, default: '' },
    comentarioTooltip: { type: String, default: '' },
  },
  computed: {
    units() {
      return buildRenderUnits(this.columnsConfig)
    },
    instituicaoLabel() {
      return this.modoFiltroCorretora === 'nome_portfolio'
        ? 'Portfólio'
        : 'Instituição'
    },
    saldoColspan() {
      return countVisibleColumns({
        visibleColumns: this.visibleColumns,
        columns: SALDO_BRUTO_COLUMNS,
      })
    },
    rentColspan() {
      return countVisibleColumns({
        visibleColumns: this.visibleColumns,
        columns: RENTABILIDADE_COLUMNS,
      })
    },
    showAcoes() {
      return shouldShowActions({
        readonly: this.readonly,
        visibleColumns: this.visibleColumns,
        allowedActions: this.allowedActions,
      })
    },
  },
  methods: {
    isVisible(column) {
      return isColumnVisible({ visibleColumns: this.visibleColumns, column })
    },
    handleSelectAll(val) {
      this.$emit('select-all', val)
    },
  },
}
</script>

<template>
  <thead>
    <tr class="assets-by-class-table__table-header">
      <template v-for="unit in units">
        <th
          v-if="
            unit.kind === 'single' &&
            unit.column.cellType === 'text' &&
            isVisible(unit.key)
          "
          :key="'cell-' + unit.key"
          scope="col"
          class="assets-by-class-table__table-header-column"
        >
          <div class="assets-by-class-table__table-header-text">
            {{ unit.column.label }}
          </div>
        </th>

        <th
          v-else-if="unit.key === 'ativo' && isVisible('ativo')"
          :key="'ativo-' + unit.key"
          scope="col"
          class="assets-by-class-table__table-header-column"
        >
          <v-simple-checkbox
            v-if="!readonly"
            class="p-0 m-0"
            dense
            hide-details
            :ripple="false"
            :value="isAllSelected"
            :indeterminate="isSomeSelected"
            color="primary"
            @input="handleSelectAll"
          />
          <div class="assets-by-class-table__table-header-text"> Ativo </div>
        </th>

        <th
          v-else-if="unit.key === 'instituicao' && isVisible('instituicao')"
          :key="'inst-' + unit.key"
          scope="col"
          class="assets-by-class-table__table-header-column"
        >
          <div class="assets-by-class-table__table-header-text">
            {{ instituicaoLabel }}
          </div>
        </th>

        <th
          v-else-if="unit.kind === 'group' && unit.group === 'Saldo Bruto' && saldoColspan > 0"
          :key="'saldo-' + unit.group"
          scope="col"
          class="assets-by-class-table__table-header-column"
          :colspan="saldoColspan"
        >
          <div class="assets-by-class-table__table-header-text">
            Saldo Bruto
          </div>
        </th>

        <th
          v-else-if="
            unit.kind === 'group' &&
            unit.group === 'Rentabilidade' &&
            showRentability &&
            rentColspan > 0
          "
          :key="'rent-' + unit.group"
          scope="col"
          class="assets-by-class-table__table-header-column"
          :colspan="rentColspan"
        >
          <div
            class="assets-by-class-table__table-header-text assets-by-class-table__table-header-text--loading"
          >
            <div> Rentabilidade </div>
            <v-progress-linear
              v-if="loadingRentAsset"
              primary
              indeterminate
              height="2"
            />
          </div>
        </th>

        <th
          v-else-if="unit.key === 'volatilidade' && isVisible('volatilidade')"
          :key="'vol-' + unit.key"
          scope="col"
          class="assets-by-class-table__table-header-column"
        >
          <div class="assets-by-class-table__table-header-text"> Vol. </div>
        </th>

        <th
          v-else-if="unit.key === 'recomendacao' && isVisible('recomendacao')"
          :key="'rec-' + unit.key"
          scope="col"
          class="assets-by-class-table__table-header-column"
        >
          <div class="assets-by-class-table__table-header-text">
            <v-tooltip bottom max-width="360">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">Recomendação</span>
              </template>
              <span>{{ recomendacaoTooltip }}</span>
            </v-tooltip>
          </div>
        </th>

        <th
          v-else-if="unit.key === 'comentario' && isVisible('comentario')"
          :key="'com-' + unit.key"
          scope="col"
          class="assets-by-class-table__table-header-column"
        >
          <div class="assets-by-class-table__table-header-text">
            <v-tooltip bottom max-width="360">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">Comentário</span>
              </template>
              <span>{{ comentarioTooltip }}</span>
            </v-tooltip>
          </div>
        </th>

        <th
          v-else-if="unit.key === 'acoes' && showAcoes"
          :key="'acoes-' + unit.key"
          scope="col"
          class="assets-by-class-table__table-header-column"
        >
          <slot name="actions-header">
            <div class="assets-by-class-table__table-header-text"> Ações </div>
          </slot>
        </th>
      </template>
    </tr>
  </thead>
</template>
